import { TEXTOS } from '../../manual-textos';
import { itensValidos, ou } from '../../manual-utils';
import {
  blocoAlerta,
  blocoCapitulo,
  blocoLista,
  blocoNota,
  blocoSecao,
  blocoTabela,
  blocoTexto,
} from '../blocos';
import type { Bloco } from '../blocos';
import type { ManualData } from '../../manual-types';

export function capitulo2(dados: ManualData): Bloco[] {
  const blocos: Bloco[] = [blocoCapitulo(2, 'Segurança')];
  blocos.push(...blocoTexto(TEXTOS.seguranca, 'c2-abertura'));

  blocos.push(blocoSecao('2.1', 'Aplicação e uso devido'));
  blocos.push(...(dados.usoDevido
    ? blocoTexto(dados.usoDevido, 'c2-uso')
    : [blocoNota('Uso devido não informado no levantamento.', 'c2-uso-vazio')]));

  blocos.push(blocoSecao('2.2', 'Aplicações proibidas e uso indevido'));
  const proibidos = itensValidos(dados.usosProibidos, ['texto']).map((item) => item.texto);
  blocos.push(...blocoLista(proibidos, 'c2-proibidos'));
  blocos.push(blocoAlerta({
    tipo: 'proibido',
    titulo: 'PROIBIDO',
    texto: 'Qualquer utilização diferente da prevista neste manual é considerada uso indevido e isenta a reconstituidora e o fornecedor de responsabilidade sobre as consequências.',
  }, 'c2-alerta-proibido'));

  blocos.push(blocoSecao('2.3', 'Precauções gerais'));
  blocos.push(...blocoLista(itensValidos(dados.precaucoes, ['texto']).map((item) => item.texto), 'c2-precaucoes'));

  blocos.push(blocoSecao('2.4', 'Equipamentos de proteção coletiva (EPC)'));
  blocos.push(...blocoTexto(TEXTOS.epcIntro, 'c2-epc-intro'));
  blocos.push(...blocoLista(itensValidos(dados.epcs, ['texto']).map((item) => item.texto), 'c2-epc'));

  blocos.push(blocoSecao('2.5', 'Equipamentos de proteção individual (EPI)'));
  blocos.push(...blocoTexto(TEXTOS.epiIntro, 'c2-epi-intro'));
  blocos.push(...blocoLista(itensValidos(dados.epis, ['texto']).map((item) => item.texto), 'c2-epi'));
  blocos.push(blocoAlerta({
    tipo: 'obrigatorio',
    titulo: 'AÇÃO OBRIGATÓRIA',
    texto: 'O uso dos equipamentos de proteção individual definidos pela contratante é obrigatório durante a operação, a limpeza e a manutenção da máquina.',
  }, 'c2-alerta-epi'));

  blocos.push(blocoSecao('2.6', 'Treinamentos'));
  blocos.push(...blocoTexto(TEXTOS.treinamentoIntro, 'c2-trein-intro'));
  blocos.push(...blocoLista(itensValidos(dados.treinamentos, ['texto']).map((item) => item.texto), 'c2-trein'));

  blocos.push(blocoSecao('2.7', 'Sinalização aplicada'));
  blocos.push(...blocoTexto(TEXTOS.sinalizacaoIntro, 'c2-sinal-intro'));
  const sinalizacao = itensValidos(dados.sinalizacao, ['tipo', 'local', 'significado']);
  blocos.push(...blocoTabela({
    id: 'c2-sinal',
    colunas: ['SINALIZAÇÃO', 'LOCAL DE APLICAÇÃO', 'SIGNIFICADO'],
    linhas: sinalizacao.map((item) => [ou(item.tipo, '—'), ou(item.local, '—'), ou(item.significado, '—')]),
    larguras: ['28%', '28%', '44%'],
    alinharEsquerda: true,
  }));

  blocos.push(blocoSecao('2.8', 'Fontes de energia'));
  blocos.push(...blocoTexto(TEXTOS.energiaIntro, 'c2-energia-intro'));
  const energias = itensValidos(dados.energias, ['tipo', 'especificacao', 'ponto']);
  blocos.push(...blocoTabela({
    id: 'c2-energia',
    colunas: ['FONTE', 'ESPECIFICAÇÃO', 'PONTO DE CORTE', 'PROCEDIMENTO DE BLOQUEIO'],
    linhas: energias.map((item) => [ou(item.tipo, '—'), ou(item.especificacao, '—'), ou(item.ponto, '—'), ou(item.desconexao, '—')]),
    larguras: ['18%', '22%', '22%', '38%'],
    alinharEsquerda: true,
    porPagina: 14,
  }));

  blocos.push(blocoSecao('2.9', 'Conexão à energia'));
  blocos.push(...blocoTexto(
    dados.conexaoEnergia || TEXTOS.conexaoEnergia,
    'c2-conexao',
  ));

  blocos.push(blocoSecao('2.10', 'Desconexão da energia'));
  blocos.push(...blocoTexto(
    dados.desconexaoEnergia || TEXTOS.desconexaoEnergia,
    'c2-desconexao',
  ));
  blocos.push(blocoAlerta({
    tipo: 'atencao',
    titulo: 'ATENÇÃO',
    texto: 'Mesmo com a alimentação desligada podem permanecer energias residuais em acumuladores, molas, cilindros e componentes elevados. Confirme a dissipação antes de iniciar a intervenção.',
  }, 'c2-alerta-energia'));

  blocos.push(blocoSecao('2.11', 'Funções de segurança'));
  blocos.push(...blocoTexto(TEXTOS.funcoesIntro, 'c2-funcoes-intro'));
  const funcoes = itensValidos(dados.funcoesSeguranca, ['denominacao', 'componente', 'descricao']);
  blocos.push(...blocoTabela({
    id: 'c2-funcoes',
    colunas: ['FUNÇÃO', 'COMPONENTE', 'CATEGORIA / PL', 'DESCRIÇÃO'],
    linhas: funcoes.map((item) => [ou(item.denominacao, '—'), ou(item.componente, '—'), ou(item.categoria, '—'), ou(item.descricao, '—')]),
    larguras: ['22%', '22%', '14%', '42%'],
    alinharEsquerda: true,
    porPagina: 14,
  }));

  blocos.push(blocoSecao('2.12', 'Perigos potenciais'));
  blocos.push(...blocoTexto(TEXTOS.perigosIntro, 'c2-perigos-intro'));
  const perigos = itensValidos(dados.perigos, ['local', 'fonte', 'perigo', 'protecao']);
  const indissociaveis = perigos.filter((item) => item.classe === 'indissociavel');
  const dissociaveis = perigos.filter((item) => item.classe !== 'indissociavel');

  blocos.push(blocoSecao('2.12.1', 'Perigos indissociáveis'));
  blocos.push(...(indissociaveis.length
    ? blocoTabela({
      id: 'c2-perigos-ind',
      colunas: ['LOCAL', 'FONTE', 'PERIGO', 'MEDIDA DE CONTROLE'],
      linhas: indissociaveis.map((item) => [ou(item.local, '—'), ou(item.fonte, '—'), ou(item.perigo, '—'), ou(item.protecao, '—')]),
      variante: 'riscos',
      larguras: ['20%', '20%', '26%', '34%'],
      alinharEsquerda: true,
      porPagina: 16,
    })
    : [blocoNota('Nenhum perigo indissociável foi identificado no levantamento.', 'c2-perigos-ind-vazio')]));

  blocos.push(blocoSecao('2.12.2', 'Perigos dissociáveis'));
  blocos.push(...(dissociaveis.length
    ? blocoTabela({
      id: 'c2-perigos-dis',
      colunas: ['LOCAL', 'FONTE', 'PERIGO', 'PROTEÇÃO APLICADA'],
      linhas: dissociaveis.map((item) => [ou(item.local, '—'), ou(item.fonte, '—'), ou(item.perigo, '—'), ou(item.protecao, '—')]),
      variante: 'riscos',
      larguras: ['20%', '20%', '26%', '34%'],
      alinharEsquerda: true,
      porPagina: 16,
    })
    : [blocoNota('Nenhum perigo dissociável foi identificado no levantamento.', 'c2-perigos-dis-vazio')]));

  blocos.push(blocoSecao('2.13', 'Emergências'));
  blocos.push(...blocoTexto(TEXTOS.emergenciaIntro, 'c2-emerg-intro'));
  const emergencias = itensValidos(dados.emergencias, ['situacao', 'acao']);
  blocos.push(...blocoTabela({
    id: 'c2-emerg',
    colunas: ['SITUAÇÃO', 'AÇÃO IMEDIATA'],
    linhas: emergencias.map((item) => [ou(item.situacao, '—'), ou(item.acao, '—')]),
    larguras: ['38%', '62%'],
    alinharEsquerda: true,
    porPagina: 14,
  }));

  return blocos;
}
